import { defineStore } from 'pinia'
import { useRequest } from 'composables/useRequest.js'

const CACHE_TTL   = 5 * 60 * 1000
const STAT_NAMES  = ['total_users', 'new_users', 'recurring_users', 'converted_users']

export const useAnalyticsTrafficStore = defineStore('analyticsTraffic', {
  state: () => ({
    // Stat cards  { [duration]: { total_users, new_users, recurring_users, converted_users } }
    stats:          {},
    statsFetchedAt: {},
    statsLoading:   false,
    statsError:     null,

    // Country/geo  { [duration]: Array<country> }
    geoData:       {},
    geoFetchedAt:  {},
    geoLoading:    false,
    geoError:      null,

    // Device breakdown  { [duration]: { platform: [], os: [], browser: [] } }
    deviceData:       {},
    deviceFetchedAt:  {},
    deviceLoading:    false,
    deviceError:      null,

    // Visitors over time  { [duration]: { labels, total, new, returning } }
    visitorsOverTime:          {},
    visitorsOverTimeFetchedAt: {},
    visitorsOverTimeLoading:   false,
    visitorsOverTimeError:     null,
  }),

  getters: {
    isStatsCacheValid:              (s) => (d) => s.statsFetchedAt[d]              != null && Date.now() - s.statsFetchedAt[d]              < CACHE_TTL,
    isGeoCacheValid:                (s) => (d) => s.geoFetchedAt[d]                != null && Date.now() - s.geoFetchedAt[d]                < CACHE_TTL,
    isDeviceCacheValid:             (s) => (d) => s.deviceFetchedAt[d]             != null && Date.now() - s.deviceFetchedAt[d]             < CACHE_TTL,
    isVisitorsOverTimeCacheValid:   (s) => (d) => s.visitorsOverTimeFetchedAt[d]   != null && Date.now() - s.visitorsOverTimeFetchedAt[d]   < CACHE_TTL,
  },

  actions: {
    async fetchAll(duration) {
      const fetches = []
      if (!this.isStatsCacheValid(duration))            fetches.push(this.fetchStats(duration))
      if (!this.isGeoCacheValid(duration))              fetches.push(this.fetchGeo(duration))
      if (!this.isDeviceCacheValid(duration))           fetches.push(this.fetchDevice(duration))
      if (!this.isVisitorsOverTimeCacheValid(duration)) fetches.push(this.fetchVisitorsOverTime(duration))
      await Promise.allSettled(fetches)
    },

    async fetchStats(duration) {
      this.statsLoading = true
      this.statsError   = null
      const { axiosRequest } = useRequest()
      const result = {}
      await Promise.allSettled(
        STAT_NAMES.map(async (name) => {
          const res = await axiosRequest('GET', `/analytics/${name}?duration=${duration}`, {}, false)
          result[name] = (!res.js_error && res.response?.success) ? res.response.data : null
        })
      )
      this.stats[duration]          = result
      this.statsFetchedAt[duration] = Date.now()
      this.statsLoading             = false
    },

    async fetchGeo(duration) {
      this.geoLoading = true
      this.geoError   = null
      const { axiosRequest } = useRequest()
      const res = await axiosRequest(
        'GET', `/api/v1/analytics/traffic/visitors_by_country?duration=${duration}`, {}, false
      )
      if (!res.js_error && res.response?.success) {
        this.geoData[duration] = res.response.data.map(p => ({ ...p, value: Math.max(p.value, 1) }))
      } else {
        this.geoError = 'Failed to load geographic data.'
      }
      this.geoFetchedAt[duration] = Date.now()
      this.geoLoading             = false
    },

    async fetchDevice(duration) {
      this.deviceLoading = true
      this.deviceError   = null
      const { axiosRequest } = useRequest()
      const [plat, os, browser] = await Promise.allSettled([
        axiosRequest('GET', `/api/v1/analytics/traffic/platform?duration=${duration}`,           {}, false),
        axiosRequest('GET', `/api/v1/analytics/traffic/operating_systems?duration=${duration}`, {}, false),
        axiosRequest('GET', `/api/v1/analytics/traffic/browsers?duration=${duration}`,          {}, false),
      ])
      const pick = (r) => r.status === 'fulfilled' && !r.value.js_error ? r.value.response.data : []
      this.deviceData[duration]       = { platform: pick(plat), os: pick(os), browser: pick(browser) }
      this.deviceFetchedAt[duration]  = Date.now()
      this.deviceLoading              = false
    },

    async fetchVisitorsOverTime(duration) {
      this.visitorsOverTimeLoading = true
      this.visitorsOverTimeError   = null
      const { axiosRequest } = useRequest()
      const res = await axiosRequest(
        'GET', `/api/v1/analytics/traffic/visitors_over_time?duration=${duration}`, {}, false
      )
      if (!res.js_error && res.response?.success) {
        this.visitorsOverTime[duration] = res.response.data
      } else {
        this.visitorsOverTimeError = 'Failed to load visitor trend data.'
      }
      this.visitorsOverTimeFetchedAt[duration] = Date.now()
      this.visitorsOverTimeLoading             = false
    },

    invalidate(duration) {
      if (duration) {
        this.statsFetchedAt[duration]            = null
        this.geoFetchedAt[duration]              = null
        this.deviceFetchedAt[duration]           = null
        this.visitorsOverTimeFetchedAt[duration] = null
      } else {
        this.statsFetchedAt            = {}
        this.geoFetchedAt              = {}
        this.deviceFetchedAt           = {}
        this.visitorsOverTimeFetchedAt = {}
      }
    },
  },
})
